import React, { useMemo, useState } from 'react';
import { useCategoryHierarchy, useFeaturedCategories, UseCategorySelectionReturn } from '../../hooks/useCategories';
import { Category } from '../../services/categoryService';
import Button from '../bootstrap/Button';
import Icon from '../icon/Icon';
import '../../styles/components/CategorySelector.scss';

/**
 * Map Font Awesome icon classes to Material Design icon names
 */
const mapFontAwesomeToMaterial = (faIcon: string): string => {
  const iconMap: Record<string, string> = {
    'fa-laptop': 'Laptop',
    'fa-mobile-alt': 'Smartphone',
    'fa-robot': 'SmartToy',
    'fa-code': 'Code',
    'fa-futbol': 'SportsSoccer',
    'fa-baseball-ball': 'SportsBaseball',
    'fa-table-tennis': 'SportsTennis',
    'fa-balance-scale': 'AccountBalance',
    'fa-film': 'Movie',
    'fa-star': 'Star',
    'fa-music': 'MusicNote',
    'fa-chart-line': 'TrendingUp',
    'fa-chart-bar': 'BarChart',
    'fa-coins': 'MonetizationOn',
    'fa-rocket': 'Flight',
    'fa-heartbeat': 'Favorite',
    'fa-graduation-cap': 'School',
    'fa-plane': 'Flight',
    'fa-utensils': 'Restaurant',
    'fa-heart': 'Favorite'
  };

  return iconMap[faIcon] || 'Category';
};

interface CategorySelectorProps {
  selection: UseCategorySelectionReturn;
  className?: string;
  showFeatured?: boolean;
  showBreadcrumb?: boolean;
  maxHeight?: string;
  /** Pre-fetched categories from a parent useCategoryHierarchy() call. When provided the
   * component skips its own API request, eliminating duplicate network calls. */
  categories?: Category[];
  categoriesLoading?: boolean;
  categoriesError?: string | null;
  onCategoriesRetry?: () => Promise<void>;
}

const CategorySelector: React.FC<CategorySelectorProps> = ({
  selection,
  className = '',
  showFeatured = true,
  showBreadcrumb = false,
  maxHeight = '400px',
  categories: propCategories,
  categoriesLoading: propCategoriesLoading,
  categoriesError: propCategoriesError,
  onCategoriesRetry,
}) => {
  // Only fetch internally when the parent has NOT supplied categories.
  // This prevents duplicate API calls when the parent already calls useCategoryHierarchy().
  const internalHierarchy = useCategoryHierarchy();
  const categories = propCategories !== undefined ? propCategories : internalHierarchy.categories;
  const isLoading = propCategories !== undefined ? (propCategoriesLoading ?? false) : internalHierarchy.isLoading;
  const error = propCategories !== undefined ? (propCategoriesError ?? null) : internalHierarchy.error;
  const refetch = propCategories !== undefined
    ? (onCategoriesRetry ?? internalHierarchy.refetch)
    : internalHierarchy.refetch;
  const { featuredCategories, isLoading: featuredLoading } = useFeaturedCategories();
  const [isExpanded, setIsExpanded] = useState(false);

  // Add debugging logs
  console.log('CategorySelector render:', {
    selectedCategories: selection.selection.selectedCategories,
    primaryCategoryId: selection.selection.primaryCategoryId,
    featuredCategoriesCount: featuredCategories?.length || 0,
    allCategoriesCount: categories?.length || 0,
    featuredLoading,
    isLoading
  });

  const selectedCategoriesData = useMemo(() => 
    selection.getSelectedCategories(categories), 
    [categories, selection]
  );

  const primaryCategoryData = useMemo(() => 
    selection.getPrimaryCategory(categories), 
    [categories, selection]
  );

  const renderFeaturedCategories = () => {
    if (!showFeatured) return null;

    // Show loading state
    if (featuredLoading) {
      return (
        <div className="featured-categories">
          <h6 className="category-selector__title mb-3">Quick Select Categories</h6>
          <div className="text-center py-3">
            <Icon icon="CircularProgress" className="me-2" />
            Loading featured categories...
          </div>
        </div>
      );
    }

    // Show message if no featured categories
    if (!featuredCategories || featuredCategories.length === 0) {
      return (
        <div className="featured-categories">
          <h6 className="category-selector__title mb-3">Quick Select Categories</h6>
          <div className="text-muted text-center py-3">
            No featured categories available
          </div>
        </div>
      );
    }

    return (
      <div className="featured-categories">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h6 className="category-selector__title mb-0">Quick Select Categories</h6>
          <Button
            color="light"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            icon={isExpanded ? 'ExpandLess' : 'ExpandMore'}
          >
            {isExpanded ? 'Collapse' : 'Show All'}
          </Button>
        </div>
        <div className="featured-category-grid">
          {featuredCategories.map(category => {
            const isSelected = selection.isSelected(category.categoryId);
            const isPrimary = selection.isPrimary(category.categoryId);
            console.log(`Category ${category.categoryName} (ID: ${category.categoryId}):`, {
              isSelected,
              isPrimary,
              selectionState: selection.selection
            });
            return (
              <button
                key={category.categoryId}
                className={`featured-category-btn ${
                  isSelected ? 'featured-category-btn--selected' : ''
                }`}
                onClick={() => {
                  selection.toggleCategory(category.categoryId);
                  // Auto-set as primary if it's the first selection
                  if (!selection.selection.primaryCategoryId && !isSelected) {
                    selection.setPrimaryCategory(category.categoryId);
                  }
                }}
                type="button"
              >
                <div 
                  className="featured-category-btn__icon"
                  style={{ 
                    backgroundColor: category.categoryColor || '#6c757d', 
                    color: '#ffffff' 
                  }}
                >
                  <Icon icon={mapFontAwesomeToMaterial(category.categoryIcon || 'fa-category')} />
                </div>
                <p className="featured-category-btn__name">
                  {category.categoryName || 'Unnamed Category'}
                </p>
                {isPrimary && (
                  <div className="featured-category-btn__primary">
                    <Icon icon="Star" size="sm" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const renderAllCategories = () => {
    if (!isExpanded) return null;
    
    if (isLoading) {
      return (
        <div className="all-categories mt-3">
          <div className="text-center py-3">
            <Icon icon="CircularProgress" className="me-2" />
            Loading all categories...
          </div>
        </div>
      );
    }

    if (!categories || categories.length === 0) {
      return (
        <div className="all-categories mt-3">
          <h6 className="category-selector__title">All Categories</h6>
          <div className="text-muted text-center py-3">
            No categories available
          </div>
        </div>
      );
    }

    const flatCategories = categories.reduce((acc: Category[], category) => {
      const addCategory = (cat: Category) => {
        acc.push(cat);
        if (cat.children) {
          cat.children.forEach(addCategory);
        }
      };
      addCategory(category);
      return acc;
    }, []);

    return (
      <div className="all-categories mt-3">
        <h6 className="category-selector__title">All Categories</h6>
        <div className="category-grid" style={{ maxHeight, overflowY: 'auto' }}>
          <div className="row g-2">
            {flatCategories.map(category => (
              <div key={category.categoryId} className="col-md-6 col-lg-4">
                <button
                  className={`category-quick-btn w-100 ${
                    selection.isSelected(category.categoryId) ? 'category-quick-btn--selected' : ''
                  }`}
                  onClick={() => {
                    selection.toggleCategory(category.categoryId);
                    // Auto-set as primary if it's the first selection
                    if (!selection.selection.primaryCategoryId && !selection.isSelected(category.categoryId)) {
                      selection.setPrimaryCategory(category.categoryId);
                    }
                  }}
                  type="button"
                >
                  <div className="d-flex align-items-center">
                    <div 
                      className="category-quick-btn__icon me-2"
                      style={{ 
                        backgroundColor: category.categoryColor || '#6c757d', 
                        color: '#ffffff' 
                      }}
                    >
                      <Icon icon={mapFontAwesomeToMaterial(category.categoryIcon || 'fa-category')} />
                    </div>
                    <div className="flex-grow-1 text-start">
                      <div className="category-quick-btn__name">
                        {category.categoryName || 'Unnamed Category'}
                      </div>
                      <small className="text-muted">{category.categoryCode}</small>
                    </div>
                    {selection.isPrimary(category.categoryId) && (
                      <Icon icon="Star" color="warning" size="sm" />
                    )}
                  </div>
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderSelectionSummary = () => {
    if (selectedCategoriesData.length === 0) return null;

    return (
      <div className="category-selection-summary">
        <div className="category-selection-summary__header">
          <h6 className="category-selection-summary__title">Selected Categories</h6>
          <div className="d-flex align-items-center gap-2">
            <span className="category-selection-summary__count">
              {selectedCategoriesData.length} selected
            </span>
            <button
              type="button"
              className="category-selection-summary__clear"
              onClick={selection.clearSelection}
            >
              Clear All
            </button>
          </div>
        </div>
        
        <div className="category-tags">
          {selectedCategoriesData.map(category => (
            <span
              key={category.categoryId}
              className={`category-tag ${
                selection.isPrimary(category.categoryId) ? 'primary' : ''
              }`}
              onClick={() => {
                if (!selection.isPrimary(category.categoryId)) {
                  selection.setPrimaryCategory(category.categoryId);
                }
              }}
              style={{ 
                borderColor: category.categoryColor,
                cursor: selection.isPrimary(category.categoryId) ? 'default' : 'pointer'
              }}
            >
              <div 
                className="category-tag__icon"
                style={{ backgroundColor: category.categoryColor, color: '#ffffff' }}
              >
                <Icon icon={mapFontAwesomeToMaterial(category.categoryIcon)} />
              </div>
              {category.categoryName}
              {!selection.isPrimary(category.categoryId) && (
                <small className="ms-1 text-muted">(click to make primary)</small>
              )}
              <button
                type="button"
                className="category-tag__remove"
                onClick={(e) => {
                  e.stopPropagation();
                  selection.toggleCategory(category.categoryId);
                }}
                aria-label={`Remove ${category.categoryName}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
        
        {primaryCategoryData && (
          <div className="mt-2">
            <small className="text-muted">
              Primary Category: <strong style={{ color: primaryCategoryData.categoryColor }}>
                {primaryCategoryData.categoryName}
              </strong>
            </small>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className={`category-selector ${className}`}>
        <div className="category-loading">
          <Icon icon="CircularProgress" className="me-2" />
          Loading categories...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`category-selector ${className}`}>
        <div className="category-error">
          <p className="category-error__message">{error}</p>
          <Button
            color="danger"
            size="sm"
            onClick={refetch}
            className="category-error__retry"
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`category-selector ${className}`}>
      <div className="category-selector__header">
        <h5 className="category-selector__title">Select Categories</h5>
        <div className="category-selector__actions">
          <Button
            color="light"
            size="sm"
            onClick={selection.clearSelection}
            isDisable={selectedCategoriesData.length === 0}
          >
            Clear All
          </Button>
        </div>
      </div>

      {renderFeaturedCategories()}
      {renderAllCategories()}
      {renderSelectionSummary()}
    </div>
  );
};

export default CategorySelector;
